import React, { useMemo } from 'react';
import { Users, AlertTriangle, RefreshCw } from 'lucide-react';
import { DonutChart, RankingTable } from '../../../components/charts';
import {
  useGetTopPerformingEmployeesQuery,
} from '../../../services/attendanceApi';
import { apiEmployeesToRankings, type ComputedDateRange } from '../useAnalyticsData';

/** Enriched employee record returned from the useMemo block */
interface EnrichedEmployee {
  empID: number;
  presentInPercentage: string;
  rank: number;
  currentPercent: number;
  prevPercent: number;
  trend: number;
  trendDirection: 'up' | 'down' | 'stable';
  riskLevel?: 'high' | 'medium' | 'low';
  [key: string]: any; // allow pass-through of other API fields
}

interface EmployeesTabProps {
  computedDateRange: ComputedDateRange;
  employeeApiType: 'regular' | 'contractual';
}

const EmployeesTab: React.FC<EmployeesTabProps> = ({
  computedDateRange,
  employeeApiType,
}) => {
  // ── Tab-specific RTK queries (only fire when this tab mounts) ──

  const { data: topEmployeesData, isLoading: isTopLoading } = useGetTopPerformingEmployeesQuery(
    {
      fromDate: computedDateRange.fromDateStr,
      toDate: computedDateRange.toDateStr,
      flag: employeeApiType,
      order: 'DESC',
      limit: 20,
    },
    { skip: !computedDateRange.isValid },
  );

  const { data: bottomEmployeesData, isLoading: isBottomLoading } = useGetTopPerformingEmployeesQuery(
    {
      fromDate: computedDateRange.fromDateStr,
      toDate: computedDateRange.toDateStr,
      flag: employeeApiType,
      order: 'ASC',
      limit: 20,
    },
    { skip: !computedDateRange.isValid },
  );

  const { data: prevEmployeesData } = useGetTopPerformingEmployeesQuery(
    {
      fromDate: computedDateRange.prevFromDateStr,
      toDate: computedDateRange.prevToDateStr,
      flag: employeeApiType,
      order: 'DESC',
      limit: 100,
    },
    { skip: !computedDateRange.isValid },
  );

  const isPerformanceLoading = isTopLoading || isBottomLoading;

  // ── Derived data (no useState sync!) ──
  const { mostImproved, highRisk, declining } = useMemo<{
    mostImproved: EnrichedEmployee[];
    highRisk: EnrichedEmployee[];
    declining: EnrichedEmployee[];
  }>(() => {
    if (!topEmployeesData?.data || !bottomEmployeesData?.data) {
      return { mostImproved: [], highRisk: [], declining: [] };
    }

    // Previous period map for trend
    const prevMap = new Map<number, any>();
    prevEmployeesData?.data?.forEach((emp: any) => prevMap.set(emp.empID, emp));

    const improved = topEmployeesData.data.map((emp: any, i: number) => {
      const prev = prevMap.get(emp.empID);
      const cur = parseFloat(emp.presentInPercentage) || 0;
      const prevP = prev ? parseFloat(prev.presentInPercentage) || 0 : cur;
      const trend = cur - prevP;

      return {
        ...emp,
        rank: i + 1,
        currentPercent: cur,
        prevPercent: prevP,
        trend: Math.round(trend * 10) / 10,
        trendDirection: trend > 1 ? 'up' : trend < -1 ? 'down' : 'stable',
      };
    });

    const dec = improved
      .filter((e: any) => e.trend < 0)
      .sort((a: any, b: any) => a.trend - b.trend)
      .slice(0, 15);

    const risk = bottomEmployeesData.data.map((emp: any, i: number) => {
      const prev = prevMap.get(emp.empID);
      const cur = parseFloat(emp.presentInPercentage) || 0;
      const prevP = prev ? parseFloat(prev.presentInPercentage) || 0 : cur;
      const trend = cur - prevP;

      return {
        ...emp,
        rank: i + 1,
        currentPercent: cur,
        prevPercent: prevP,
        trend: Math.round(trend * 10) / 10,
        trendDirection: trend > 1 ? 'up' : trend < -1 ? 'down' : 'stable',
        riskLevel: cur < 50 ? 'high' : cur < 75 ? 'medium' : 'low',
      };
    });

    return { mostImproved: improved, highRisk: risk, declining: dec };
  }, [topEmployeesData, bottomEmployeesData, prevEmployeesData]);

  // Attendance level distribution
  const levelDistribution = useMemo(() => {
    const all = [...mostImproved, ...highRisk];
    return [
      {
        name: 'Excellent (≥85%)',
        value: all.filter(e => (parseFloat(e.presentInPercentage) || 0) >= 85).length,
        color: '#10b981',
      },
      {
        name: 'Good (70-85%)',
        value: all.filter(e => {
          const p = parseFloat(e.presentInPercentage) || 0;
          return p >= 70 && p < 85;
        }).length,
        color: '#f59e0b',
      },
      {
        name: 'Low (<70%)',
        value: highRisk.filter(e => (parseFloat(e.presentInPercentage) || 0) < 70).length,
        color: '#ef4444',
      },
    ];
  }, [mostImproved, highRisk]);

  const trendDistribution = useMemo(
    () => [
      { name: 'Improving', value: mostImproved.filter(e => e.trend > 1).length, color: '#10b981' },
      {
        name: 'Stable',
        value: mostImproved.filter(e => e.trend >= -1 && e.trend <= 1).length,
        color: '#6366f1',
      },
      {
        name: 'Declining',
        value: [...mostImproved, ...highRisk].filter(e => e.trend < -1).length,
        color: '#ef4444',
      },
    ],
    [mostImproved, highRisk],
  );

  const riskDistribution = useMemo(
    () => [
      {
        name: 'Low Risk',
        value: highRisk.filter(
          e => e.riskLevel === 'low' || (parseFloat(e.presentInPercentage) || 0) >= 75,
        ).length,
        color: '#10b981',
      },
      {
        name: 'Medium Risk',
        value: highRisk.filter(e => {
          const p = parseFloat(e.presentInPercentage) || 0;
          return e.riskLevel === 'medium' || (p >= 50 && p < 75);
        }).length,
        color: '#f59e0b',
      },
      {
        name: 'High Risk',
        value: highRisk.filter(
          e => e.riskLevel === 'high' || (parseFloat(e.presentInPercentage) || 0) < 50,
        ).length,
        color: '#ef4444',
      },
    ],
    [highRisk],
  );

  const highRiskBelow50 = useMemo(
    () =>
      highRisk.filter(
        e => (e.currentPercent || parseFloat(e.presentInPercentage)) < 50,
      ),
    [highRisk],
  );

  const LoadingSpinner = () => (
    <div className="h-[250px] flex items-center justify-center text-gray-500">
      <RefreshCw className="w-5 h-5 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Employee Count Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">
              Showing{' '}
              <span className="text-purple-600 font-semibold">
                {employeeApiType === 'regular' ? 'Regular' : 'Contractual'}
              </span>{' '}
              Employees
            </span>
          </div>
        </div>
      </div>

      {/* Employee Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">By Attendance Level</h3>
          {isPerformanceLoading ? (
            <LoadingSpinner />
          ) : (
            <DonutChart
              data={levelDistribution}
              height={250}
              centerLabel="Total"
              centerValue={mostImproved.length + highRisk.length}
            />
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">By Trend</h3>
          {isPerformanceLoading ? (
            <LoadingSpinner />
          ) : (
            <DonutChart
              data={trendDistribution}
              height={250}
              centerLabel="Total"
              centerValue={mostImproved.length}
            />
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">By Risk Level</h3>
          {isPerformanceLoading ? (
            <LoadingSpinner />
          ) : (
            <DonutChart
              data={riskDistribution}
              height={250}
              centerLabel="At Risk"
              centerValue={highRisk.length}
            />
          )}
        </div>
      </div>

      {/* Employee Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {isPerformanceLoading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading top performers...
            </div>
          ) : (
            <RankingTable
              data={apiEmployeesToRankings(mostImproved.slice(0, 15))}
              title="📈 Top Performers (Selected Period)"
              limit={10}
              highlightTop={3}
              emptyMessage="No data available for selected period"
            />
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {isPerformanceLoading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading declining attendance...
            </div>
          ) : (
            <RankingTable
              data={apiEmployeesToRankings(
                declining.length > 0
                  ? declining
                  : highRisk.filter(e => e.trend < 0).slice(0, 15),
              )}
              title="📉 Declining Attendance"
              limit={10}
              highlightBottom={10}
              emptyMessage="No employees with declining attendance found"
            />
          )}
        </div>
      </div>

      {/* High Risk Employees */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <h3 className="text-lg font-semibold text-red-800">
            High Risk Employees ({highRiskBelow50.length})
          </h3>
          <span className="text-sm text-red-600 ml-2">(Employees with &lt;50% attendance)</span>
        </div>
        {isPerformanceLoading ? (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading high risk employees...
          </div>
        ) : (
          <RankingTable
            data={apiEmployeesToRankings(highRiskBelow50)}
            limit={20}
            highlightBottom={20}
            emptyMessage="No high-risk employees found in the selected period"
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(EmployeesTab);
